import { useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';
import { useLightPathEnabled } from '../../hooks/useFeatureFlags';
import type { LevelBadge } from '../../types/auth';
import {
  isSpiritualLevelCode,
  type LevelBadgeLoadStatus,
} from './levelBadgeContract';

/**
 * CORE-269 — resolve level badges for users, shared across every surface that
 * renders a name (profile, comments, community, cards).
 *
 * A module-level cache dedupes across surfaces, and requests are MICRO-BATCHED:
 * every id asked for within the same tick (e.g. one row per card in a list, each
 * calling useLevelBadge) is coalesced into a single `POST /gamification/badges`
 * on the next microtask — so a 30-card list makes ONE request, not 30, without
 * callers having to thread a batch down. Best-effort: a failed fetch just leaves
 * the badge absent (no chip), never throws into the render tree.
 */
const cache = new Map<string, LevelBadge>();
const pending = new Set<string>();
const inFlight = new Set<string>();
const lastStatus = new Map<string, Exclude<LevelBadgeLoadStatus, 'disabled'>>();
let flushScheduled = false;

// Bump on every cache write so mounted consumers re-render with fresh badges.
let version = 0;
const listeners = new Set<() => void>();

const isValidLevelBadge = (value: unknown): value is LevelBadge => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<LevelBadge>;
  return isSpiritualLevelCode(candidate.level)
    && typeof candidate.name === 'string'
    && candidate.name.trim().length > 0;
};

function bump() {
  version += 1;
  listeners.forEach((l) => l());
}

async function flush() {
  flushScheduled = false;
  const ids = [...pending].filter((id) => !cache.has(id) && !inFlight.has(id));
  pending.clear();
  if (ids.length === 0) return;
  ids.forEach((id) => inFlight.add(id));
  try {
    const map = await api.getLevelBadges(ids);
    for (const id of ids) {
      const badge = map?.[id];
      if (isValidLevelBadge(badge)) {
        cache.set(id, badge);
        lastStatus.set(id, 'ready');
      } else {
        // Keep the slot but render no fake level. Missing/malformed data is a
        // settled response, so remember it and do not POST it again on remount.
        lastStatus.set(id, 'empty');
      }
    }
  } catch {
    ids.forEach((id) => lastStatus.set(id, 'error'));
  } finally {
    ids.forEach((id) => inFlight.delete(id));
    bump();
  }
}

function requestBadges(ids: string[]) {
  let queued = false;
  for (const id of ids) {
    if (
      id
      && !cache.has(id)
      && lastStatus.get(id) !== 'empty'
      && !pending.has(id)
      && !inFlight.has(id)
    ) {
      pending.add(id);
      lastStatus.set(id, 'loading');
      queued = true;
    }
  }
  if (queued && !flushScheduled) {
    flushScheduled = true;
    // Microtask: coalesces every id requested by this commit's effects into one call.
    Promise.resolve().then(flush);
  }
}

/**
 * Batch-resolve badges for a list of user ids. Returns a userId → badge map.
 *
 * CORE-273 / CORE-664 — the badge is a «Путь Света» surface like the tab and the
 * profile card, so it lives behind the same runtime `light_path` flag. With the
 * flag off the hook resolves to an empty map (no chip anywhere: profile, cards,
 * comments, chat) and makes no request at all — otherwise a prod build with
 * gamification switched off still showed «Искатель» next to every name.
 */
export interface LevelBadgesState {
  badges: Record<string, LevelBadge>;
  statuses: Record<string, LevelBadgeLoadStatus>;
}

export interface LevelBadgeState {
  badge: LevelBadge | null;
  status: LevelBadgeLoadStatus;
}

/**
 * Status-aware lookup for stable presentation. Every eligible id starts in a
 * reserved `loading` slot before the effect queues its request; invalid/error
 * results keep that same slot without exposing any badge content.
 */
export function useLevelBadgesState(userIds: (string | null | undefined)[]): LevelBadgesState {
  const [, setTick] = useState(0);
  const lightPathEnabled = useLightPathEnabled();
  const requestedKey = userIds.filter(Boolean).join(',');
  const key = lightPathEnabled ? requestedKey : '';
  // Depend on the stable `key` string, not the array identity (a fresh array every
  // render would re-run forever). eslint-disable: `key` is the value of `userIds`.
  const ids = useMemo(
    () => (lightPathEnabled ? Array.from(new Set(userIds.filter(Boolean) as string[])) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, lightPathEnabled],
  );

  // Subscribe to cache updates so a fetch triggered elsewhere refreshes us too.
  useEffect(() => {
    const l = () => setTick(version);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);

  useEffect(() => {
    requestBadges(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const out: Record<string, LevelBadge> = {};
  const statuses: Record<string, LevelBadgeLoadStatus> = {};
  for (const id of Array.from(new Set(userIds.filter(Boolean) as string[]))) {
    statuses[id] = lightPathEnabled
      ? (cache.has(id) ? 'ready' : (lastStatus.get(id) ?? 'loading'))
      : 'disabled';
  }
  for (const id of ids) {
    const b = cache.get(id);
    if (b) out[id] = b;
  }
  return { badges: out, statuses };
}

/** Legacy map-only API for call-sites that do not render a visual slot. */
export function useLevelBadges(userIds: (string | null | undefined)[]): Record<string, LevelBadge> {
  return useLevelBadgesState(userIds).badges;
}

/** Resolve a single user's badge (thin wrapper — micro-batched with its siblings). */
export function useLevelBadge(userId?: string | null): LevelBadge | null {
  return useLevelBadgeState(userId).badge;
}

/** Resolve a single badge plus the state that owns its stable layout slot. */
export function useLevelBadgeState(userId?: string | null): LevelBadgeState {
  const result = useLevelBadgesState(userId ? [userId] : []);
  return userId
    ? { badge: result.badges[userId] ?? null, status: result.statuses[userId] ?? 'disabled' }
    : { badge: null, status: 'disabled' };
}
